import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const footerNav = (path) => (e) => { e.preventDefault(); navigate(path); };

  return (
    <footer className="bg-[#003366] text-white py-14 px-6" role="contentinfo">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10 mb-10">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-bold mb-4">WorkNation Labour Ltd</h3>
            <p className="text-gray-300 leading-relaxed text-sm mb-4">
              WorkNation Labour Ltd is a staffing agency based in Vancouver, British Columbia. We connect qualified workers with employers across BC, specializing in construction, warehouse, agriculture, transportation, and cold storage staffing. Over 200 workers placed.
            </p>
            <div className="flex items-start text-sm text-gray-300">
              <MapPin size={16} className="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
              Vancouver, British Columbia, Canada
            </div>
            {/* Social Links */}
            <div className="flex items-center gap-3 mt-4">
              <a href="https://www.facebook.com/people/Worknation-Labour-Ltd/61583739914316/#" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-200" aria-label="Facebook">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://ca.linkedin.com/company/worknation-labour-ltd?trk=public_profile_topcard-current-company" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-200" aria-label="LinkedIn">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://maps.app.goo.gl/tFG1A32K6rk1YHn67" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-200" aria-label="Google Maps">
                <MapPin size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: 'Home', path: '/' },
                { label: 'Employers', path: '/employers' },
                { label: 'Job Seekers', path: '/job-seekers' },
                { label: 'About Us', path: '/about' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Blog', path: '/blog' },
              ].map((link) => (
                <li key={link.path}>
                  <a href={link.path} onClick={footerNav(link.path)} className="text-gray-300 hover:text-white transition-colors duration-300">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="text-lg font-bold mb-4">Industries</h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: 'Construction', path: '/industries/construction' },
                { label: 'Warehouse & Fulfillment', path: '/industries/warehouse' },
                { label: 'Food Processing', path: '/industries/food-processing' },
                { label: 'Agriculture', path: '/industries/agriculture' },
                { label: 'Transportation', path: '/industries/transportation' },
                { label: 'Cold Storage', path: '/industries/cold-storage' },
              ].map((link) => (
                <li key={link.path}>
                  <a href={link.path} onClick={footerNav(link.path)} className="text-gray-300 hover:text-white transition-colors duration-300">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="text-lg font-bold mb-3 mt-6">Locations</h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: 'Vancouver', path: '/locations/vancouver' },
                { label: 'Surrey', path: '/locations/surrey' },
                { label: 'Burnaby', path: '/locations/burnaby' },
                { label: 'Richmond', path: '/locations/richmond' },
                { label: 'Delta', path: '/locations/delta' },
                { label: 'Langley', path: '/locations/langley' },
                { label: 'Abbotsford', path: '/locations/abbotsford' },
                { label: 'Coquitlam', path: '/locations/coquitlam' },
              ].map((link) => (
                <li key={link.path}>
                  <a href={link.path} onClick={footerNav(link.path)} className="text-gray-300 hover:text-white transition-colors duration-300">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-4">Contact Us</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start">
                <Mail size={16} className="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div className="text-gray-300">
                  <p className="font-semibold text-white mb-0.5">For Employers:</p>
                  <a href="mailto:info@worknation.ca" className="hover:text-white transition-colors duration-300 break-all">
                    info@worknation.ca
                  </a>
                </div>
              </li>
              <li className="flex items-start">
                <Mail size={16} className="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div className="text-gray-300">
                  <p className="font-semibold text-white mb-0.5">For Job Seekers:</p>
                  <a href="mailto:resumeworknation@gmail.com" className="hover:text-white transition-colors duration-300 break-all">
                    resumeworknation@gmail.com
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-600 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p>&copy; {currentYear} WorkNation Labour Ltd. All Rights Reserved.</p>
          <div className="flex gap-4 mt-3 md:mt-0">
            <a href="/privacy-policy" onClick={footerNav('/privacy-policy')} className="hover:text-white transition-colors duration-300">
              Privacy Policy
            </a>
            <span className="text-gray-600">|</span>
            <a href="/terms-of-service" onClick={footerNav('/terms-of-service')} className="hover:text-white transition-colors duration-300">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
